/**
 * ET Header
 * - "Shop all" mega menu: opens on hover (with a short hover-intent delay) and
 *   toggles on click (hover mode), or toggles on click only (click mode).
 *   Closed on Escape / outside click / page scroll.
 * - Style mega menu: same hover-intent + debounce behaviour, trigger is a real link.
 * - Mobile drawer toggle (multi-level drill-down via data-mm-back="<panel>").
 * - Sticky shadow on scroll.
 */
if (!customElements.get('et-header')) {
  const HOVER_OPEN_DELAY = 160; // ms the pointer has to rest on a trigger before a panel opens
  const HOVER_CLOSE_DELAY = 180; // ms grace period when the pointer leaves trigger/panel
  const LOAD_GUARD = 400; // ms after load during which a (synthetic) hover does not open a panel
  const SCROLL_CLOSE_DISTANCE = 12; // px of page scroll after which open panels close

  class ETHeader extends HTMLElement {
    constructor() {
      super();
      this.trigger = this.dataset.trigger || 'hover';
      this.closeTimer = null;
      this.openTimer = null;
      this.styleTimer = null;
      this.styleOpenTimer = null;
      this.readyAt = 0;
      this.lastScrollY = 0;
      this.onDocClick = this.onDocClick.bind(this);
      this.onKeyup = this.onKeyup.bind(this);
      this.onScroll = this.onScroll.bind(this);
    }

    connectedCallback() {
      this.shopAll = this.querySelector('.et-shop-all');
      this.megaMenu = this.querySelector('.et-mega-menu');
      this.styleTrigger = this.querySelector('.et-style-trigger');
      this.styleMenu = this.querySelector('.et-style-menu');
      this.mobileToggle = this.querySelector('.et-mobile-toggle');
      this.mobileMenu = this.querySelector('.et-mobile-menu');
      this.overlay = this.querySelector('.et-header__overlay');
      this.readyAt = Date.now();
      this.lastScrollY = window.scrollY;

      this.bindMegaMenu();
      this.bindStyleMenu();
      this.bindMobileMenu();

      document.addEventListener('keyup', this.onKeyup);
      document.addEventListener('click', this.onDocClick);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }

    disconnectedCallback() {
      document.removeEventListener('keyup', this.onKeyup);
      document.removeEventListener('click', this.onDocClick);
      window.removeEventListener('scroll', this.onScroll);
    }

    // Ignore hovers in the first moments after load: browsers fire a synthetic
    // mouseover for the element that happens to sit under a resting cursor.
    hoverAllowed() {
      return Date.now() - this.readyAt > LOAD_GUARD;
    }

    /* ---------------- Mega menu ---------------- */
    bindMegaMenu() {
      if (!this.shopAll || !this.megaMenu) return;
      this.megaMenu.hidden = false;

      // Click always toggles.
      this.shopAll.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        clearTimeout(this.openTimer);
        this.toggleMega();
      });

      this.bindCategories();

      // Hover mode: open after a short rest on the trigger, close (debounced)
      // on leave of trigger + panel.
      if (this.trigger === 'hover') {
        const leave = () => {
          clearTimeout(this.openTimer);
          clearTimeout(this.closeTimer);
          this.closeTimer = setTimeout(() => this.closeMega(), HOVER_CLOSE_DELAY);
        };
        this.shopAll.addEventListener('mouseenter', () => {
          clearTimeout(this.closeTimer);
          if (this.classList.contains('et-mega-open')) return;
          if (!this.hoverAllowed()) return;
          clearTimeout(this.openTimer);
          this.openTimer = setTimeout(() => this.openMega(), HOVER_OPEN_DELAY);
        });
        this.shopAll.addEventListener('mouseleave', leave);
        this.megaMenu.addEventListener('mouseenter', () => {
          clearTimeout(this.closeTimer);
        });
        this.megaMenu.addEventListener('mouseleave', leave);
      }
    }

    // Left-rail category tabs swap the visible panel.
    bindCategories() {
      this.cats = Array.from(this.megaMenu.querySelectorAll('[data-mega-cat]'));
      this.panels = Array.from(this.megaMenu.querySelectorAll('[data-mega-panel]'));
      if (!this.cats.length) return;

      this.cats.forEach((cat) => {
        const activate = () => this.selectCategory(cat.dataset.megaCat);
        cat.addEventListener('mouseenter', activate);
        cat.addEventListener('focus', activate);
      });
    }

    selectCategory(id) {
      this.cats.forEach((cat) => {
        const active = cat.dataset.megaCat === id;
        cat.classList.toggle('is-active', active);
        cat.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      this.panels.forEach((panel) => {
        const active = panel.dataset.megaPanel === id;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
      });
    }

    openMega() {
      clearTimeout(this.openTimer);
      this.closeStyle();
      this.lastScrollY = window.scrollY;
      this.classList.add('et-mega-open');
      this.shopAll.setAttribute('aria-expanded', 'true');
    }

    closeMega() {
      clearTimeout(this.openTimer);
      this.classList.remove('et-mega-open');
      if (this.shopAll) this.shopAll.setAttribute('aria-expanded', 'false');
    }

    toggleMega() {
      clearTimeout(this.closeTimer);
      if (this.classList.contains('et-mega-open')) {
        this.closeMega();
      } else {
        this.openMega();
      }
    }

    /* ---------------- Style menu ---------------- */
    bindStyleMenu() {
      if (!this.styleTrigger || !this.styleMenu) return;
      this.styleMenu.hidden = false;

      const leave = () => {
        clearTimeout(this.styleOpenTimer);
        clearTimeout(this.styleTimer);
        this.styleTimer = setTimeout(() => this.closeStyle(), HOVER_CLOSE_DELAY);
      };

      // Pointer: open after a short rest on the trigger; the panel only keeps it open.
      this.styleTrigger.addEventListener('mouseenter', () => {
        clearTimeout(this.styleTimer);
        if (this.classList.contains('et-style-open')) return;
        if (!this.hoverAllowed()) return;
        clearTimeout(this.styleOpenTimer);
        this.styleOpenTimer = setTimeout(() => this.openStyle(), HOVER_OPEN_DELAY);
      });
      this.styleTrigger.addEventListener('mouseleave', leave);
      this.styleMenu.addEventListener('mouseenter', () => {
        clearTimeout(this.styleTimer);
      });
      this.styleMenu.addEventListener('mouseleave', leave);

      // Keyboard: open while focus is inside trigger or panel.
      const focusEnter = () => {
        clearTimeout(this.styleTimer);
        this.openStyle();
      };
      this.styleTrigger.addEventListener('focus', focusEnter);
      this.styleMenu.addEventListener('focusin', focusEnter);
      [this.styleTrigger, this.styleMenu].forEach((el) => {
        el.addEventListener('focusout', (e) => {
          const next = e.relatedTarget;
          if (next && (this.styleMenu.contains(next) || this.styleTrigger.contains(next))) return;
          leave();
        });
      });

      this.styleCats = Array.from(this.styleMenu.querySelectorAll('[data-style-cat]'));
      this.stylePanels = Array.from(this.styleMenu.querySelectorAll('[data-style-panel]'));
      this.styleCats.forEach((cat) => {
        const activate = () => this.selectStyle(cat.dataset.styleCat);
        cat.addEventListener('mouseenter', activate);
        cat.addEventListener('focus', activate);
      });
    }

    selectStyle(id) {
      this.styleCats.forEach((cat) => {
        const active = cat.dataset.styleCat === id;
        cat.classList.toggle('is-active', active);
        cat.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      this.stylePanels.forEach((panel) => {
        const active = panel.dataset.stylePanel === id;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
      });
    }

    openStyle() {
      if (!this.styleMenu) return;
      clearTimeout(this.styleOpenTimer);
      clearTimeout(this.closeTimer);
      this.closeMega();
      this.lastScrollY = window.scrollY;
      this.classList.add('et-style-open');
      this.styleTrigger.setAttribute('aria-expanded', 'true');
    }

    closeStyle() {
      if (!this.styleMenu) return;
      clearTimeout(this.styleOpenTimer);
      this.classList.remove('et-style-open');
      this.styleTrigger.setAttribute('aria-expanded', 'false');
    }

    closePanels() {
      clearTimeout(this.openTimer);
      clearTimeout(this.styleOpenTimer);
      this.closeMega();
      this.closeStyle();
    }

    /* ---------------- Mobile drawer ---------------- */
    bindMobileMenu() {
      if (!this.mobileToggle || !this.mobileMenu) return;

      this.mobileToggle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggleMobile();
      });

      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.closeMobile());
      }

      this.mmPanels = Array.from(this.mobileMenu.querySelectorAll('[data-mm-panel]'));
      this.mmPanels.forEach((panel) => {
        panel.hidden = false;
      });

      // Drill into a panel.
      this.mobileMenu.querySelectorAll('[data-mm-drill]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.showMobilePanel(btn.dataset.mmDrill);
        });
      });

      // Back to the parent panel (data-mm-back="<panel>"), default root.
      this.mobileMenu.querySelectorAll('[data-mm-back]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.showMobilePanel(btn.dataset.mmBack || 'root');
        });
      });

      // Close from any panel header.
      this.mobileMenu.querySelectorAll('[data-mm-close]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.closeMobile();
        });
      });
    }

    showMobilePanel(id) {
      if (!this.mmPanels) return;
      const target = this.mmPanels.find((panel) => panel.dataset.mmPanel === id);
      const backBtn = target && target.querySelector('[data-mm-back]');
      const parentId = backBtn && backBtn.dataset.mmBack && backBtn.dataset.mmBack !== 'root' ? backBtn.dataset.mmBack : null;
      this.mmPanels.forEach((panel) => {
        panel.classList.toggle('is-active', panel.dataset.mmPanel === id);
        panel.classList.toggle('is-parent', panel.dataset.mmPanel === parentId);
      });
    }

    openMobile() {
      this.classList.add('et-mobile-open');
      this.mobileToggle.setAttribute('aria-expanded', 'true');
      this.mobileMenu.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
    }

    closeMobile() {
      this.classList.remove('et-mobile-open');
      this.mobileToggle.setAttribute('aria-expanded', 'false');
      this.mobileMenu.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
      setTimeout(() => this.showMobilePanel('root'), 300);
    }

    toggleMobile() {
      if (this.classList.contains('et-mobile-open')) {
        this.closeMobile();
      } else {
        this.openMobile();
      }
    }

    /* ---------------- Global handlers ---------------- */
    onKeyup(e) {
      if (e.code && e.code.toUpperCase() === 'ESCAPE') {
        this.closePanels();
        this.closeMobile();
      }
    }

    onDocClick(e) {
      if (
        this.classList.contains('et-style-open') &&
        !this.styleMenu.contains(e.target) &&
        !this.styleTrigger.contains(e.target)
      ) {
        this.closeStyle();
      }
      if (
        this.classList.contains('et-mega-open') &&
        !this.megaMenu.contains(e.target) &&
        !this.shopAll.contains(e.target)
      ) {
        this.closeMega();
      }
    }

    onScroll() {
      const y = window.scrollY;
      if (this.classList.contains('et-header-sticky--active')) {
        this.classList.toggle('et-stuck', y > 4);
      }
      // A page scroll means the visitor is reading the page, not the menu: close open panels.
      if (Math.abs(y - this.lastScrollY) > SCROLL_CLOSE_DISTANCE) {
        this.lastScrollY = y;
        if (this.classList.contains('et-mega-open') || this.classList.contains('et-style-open')) {
          this.closePanels();
        }
      }
    }
  }

  customElements.define('et-header', ETHeader);
}
